/**
 * Knod Export Service
 * Handles exporting and importing Knods (flows) as .knod files or .zip bundles
 */

import JSZip from 'jszip';
import type { Node, Edge, Viewport } from '@xyflow/react';
import flowSerializationService from './FlowSerializationService';
import logger from '../utils/logger';

export interface KnodExportOptions {
  flowName: string;
  nodes: Node[];
  edges: Edge[];
  viewport: Viewport;
  flowId?: string;
}

export interface KnodImportResult {
  flowName: string;
  nodes: Node[];
  edges: Edge[];
  viewport: Viewport;
  mediaFiles?: Map<string, Blob>;
}

class KnodExportService {
  /**
   * Export as .knod file (JSON structure only, no media references)
   */
  async exportStructureOnly(options: KnodExportOptions): Promise<void> {
    const { flowName, nodes, edges, viewport, flowId } = options;
    
    try {
      // Strip media references from nodes before serializing
      const cleanedNodes = this.stripMediaReferences(nodes);
      
      // Serialize the flow
      const savedFlow = flowSerializationService.serializeFlow(
        cleanedNodes,
        edges,
        viewport,
        {
          id: flowId,
          name: flowName,
        },
        false
      );
      
      // Convert to JSON
      const jsonString = flowSerializationService.exportToJSON(savedFlow);
      
      // Create and download the file
      const blob = new Blob([jsonString], { type: 'application/json' });
      this.downloadBlob(blob, this.sanitizeFilename(flowName) + '.knod');
      
      logger.info('Knod exported successfully (structure only)');
    } catch (error) {
      logger.error('Failed to export Knod:', error);
      throw new Error('Failed to export Knod');
    }
  }
  
  /**
   * Export as .zip file with all media files
   */
  async exportWithMedia(options: KnodExportOptions): Promise<void> {
    const { flowName, nodes, edges, viewport, flowId } = options;
    
    try {
      // Serialize the flow
      const savedFlow = flowSerializationService.serializeFlow(
        nodes,
        edges,
        viewport,
        {
          id: flowId,
          name: flowName,
        },
        false
      );
      
      // Create ZIP
      const zip = new JSZip();
      
      // Add the knod JSON file
      const jsonString = flowSerializationService.exportToJSON(savedFlow);
      zip.file('knod.json', jsonString);
      
      // Collect and add all media files
      const mediaFolder = zip.folder('media');
      if (mediaFolder) {
        const mediaMap = await this.collectMediaFiles(nodes);
        
        for (const [filename, blob] of mediaMap.entries()) {
          mediaFolder.file(filename, blob);
        }
        
        // Update the JSON with relative media paths
        const updatedFlow = this.updateMediaReferences(savedFlow, mediaMap);
        zip.file('knod.json', flowSerializationService.exportToJSON(updatedFlow));
      }
      
      // Generate ZIP and download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      this.downloadBlob(zipBlob, this.sanitizeFilename(flowName) + '.zip');
      
      logger.info('Knod exported successfully (with media)');
    } catch (error) {
      logger.error('Failed to export Knod with media:', error);
      throw new Error('Failed to export Knod with media');
    }
  }
  
  /**
   * Import a .knod file (JSON)
   */
  async importKnodFile(file: File): Promise<KnodImportResult> {
    try {
      const text = await file.text();
      const savedFlow = flowSerializationService.importFromJSON(text);
      const state = flowSerializationService.deserializeFlow(savedFlow);
      
      return {
        flowName: savedFlow.metadata.name,
        nodes: state.nodes,
        edges: state.edges,
        viewport: state.viewport,
      };
    } catch (error) {
      logger.error('Failed to import Knod file:', error);
      throw new Error('Failed to import Knod file');
    }
  }
  
  /**
   * Import a .zip file with media
   */
  async importZipFile(file: File): Promise<KnodImportResult> {
    try {
      const zip = await JSZip.loadAsync(file);
      
      // Find and read the knod.json file
      const knodJsonFile = zip.file('knod.json');
      if (!knodJsonFile) {
        throw new Error('Invalid Knod zip: missing knod.json');
      }
      
      const jsonText = await knodJsonFile.async('text');
      const savedFlow = flowSerializationService.importFromJSON(jsonText);
      const state = flowSerializationService.deserializeFlow(savedFlow);
      
      // Extract media files
      const mediaFiles = new Map<string, Blob>();
      const mediaFolder = zip.folder('media');
      
      if (mediaFolder) {
        const files = Object.keys(zip.files).filter(path => path.startsWith('media/') && !path.endsWith('/'));
        
        for (const filePath of files) {
          const zipFile = zip.file(filePath);
          if (zipFile) {
            const blob = await zipFile.async('blob');
            const filename = filePath.replace('media/', '');
            mediaFiles.set(filename, blob);
          }
        }
      }
      
      return {
        flowName: savedFlow.metadata.name,
        nodes: state.nodes,
        edges: state.edges,
        viewport: state.viewport,
        mediaFiles,
      };
    } catch (error) {
      logger.error('Failed to import Knod zip:', error);
      throw new Error('Failed to import Knod zip');
    }
  }
  
  /**
   * Import from file (auto-detect type)
   */
  async importFromFile(file: File): Promise<KnodImportResult> {
    const extension = file.name.toLowerCase().split('.').pop();
    
    if (extension === 'knod' || extension === 'json') {
      return this.importKnodFile(file);
    } else if (extension === 'zip') {
      return this.importZipFile(file);
    } else {
      throw new Error('Unsupported file type. Please use .knod or .zip files.');
    }
  }
  
  /**
   * Collect all media URLs from nodes and fetch them as blobs
   */
  private async collectMediaFiles(nodes: Node[]): Promise<Map<string, Blob>> {
    const mediaMap = new Map<string, Blob>();
    const urlsToFetch: { url: string; filename: string; nodeId: string }[] = [];
    
    for (const node of nodes) {
      const data = node.data as Record<string, unknown>;
      
      // Check for common media URL patterns in node data
      const urlKeys = ['imageUrl', 'videoUrl', 'audioUrl', 'mediaUrl', 'url', 'output', 'result'];
      
      for (const key of urlKeys) {
        const value = data[key];
        if (typeof value === 'string' && this.isMediaUrl(value)) {
          const extension = this.getExtensionFromUrl(value);
          const filename = `${node.id}_${key}${extension}`;
          urlsToFetch.push({ url: value, filename, nodeId: node.id });
        }
      }
      
      // Check for paged outputs (array of image/video URLs)
      if (Array.isArray(data.pagedOutputs)) {
        for (let i = 0; i < data.pagedOutputs.length; i++) {
          const pageOutput = data.pagedOutputs[i];
          if (typeof pageOutput === 'string' && this.isMediaUrl(pageOutput)) {
            const extension = this.getExtensionFromUrl(pageOutput);
            const filename = `${node.id}_page${i}${extension}`;
            urlsToFetch.push({ url: pageOutput, filename, nodeId: node.id });
          }
        }
      }
    }
    
    // Fetch all media files in parallel (with some concurrency limit)
    const fetchPromises = urlsToFetch.map(async ({ url, filename }) => {
      try {
        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          return { filename, blob };
        }
      } catch (error) {
        logger.warn(`Failed to fetch media file ${filename}:`, error);
      }
      return null;
    });
    
    const results = await Promise.all(fetchPromises);
    
    for (const result of results) {
      if (result) {
        mediaMap.set(result.filename, result.blob);
      }
    }
    
    return mediaMap;
  }
  
  /**
   * Update node media references to point to local media files
   */
  private updateMediaReferences(savedFlow: ReturnType<typeof flowSerializationService.serializeFlow>, mediaMap: Map<string, Blob>): typeof savedFlow {
    // Create a mapping of original URLs to local paths
    const updatedNodes = savedFlow.state.nodes.map(node => {
      const data = { ...node.data } as Record<string, unknown>;
      const urlKeys = ['imageUrl', 'videoUrl', 'audioUrl', 'mediaUrl', 'url', 'output', 'result'];
      
      for (const key of urlKeys) {
        const value = data[key];
        if (typeof value === 'string' && this.isMediaUrl(value)) {
          const extension = this.getExtensionFromUrl(value);
          const filename = `${node.id}_${key}${extension}`;
          if (mediaMap.has(filename)) {
            // Store both the original URL and local filename
            data[`_original_${key}`] = value;
            data[`_local_${key}`] = `media/${filename}`;
          }
        }
      }
      
      return { ...node, data };
    });
    
    return {
      ...savedFlow,
      state: {
        ...savedFlow.state,
        nodes: updatedNodes,
      },
    };
  }
  
  /**
   * Strip media references from nodes for structure-only export
   * Only removes media URLs (images, videos, audio) - preserves all text content and prompts
   */
  private stripMediaReferences(nodes: Node[]): Node[] {
    // Only these keys are checked for media URLs - text content is always preserved
    const potentialMediaKeys = ['imageUrl', 'videoUrl', 'audioUrl', 'mediaUrl', 'output', 'result', 'url'];
    
    return nodes.map(node => {
      const data = { ...node.data } as Record<string, unknown>;
      
      // Only remove fields that contain actual media URLs
      for (const key of potentialMediaKeys) {
        const value = data[key];
        // Only strip if it's a media URL - text outputs are preserved
        if (typeof value === 'string' && this.isMediaUrl(value)) {
          delete data[key];
        }
      }
      
      // Clear paged outputs only if they contain media URLs (not text)
      if (Array.isArray(data.pagedOutputs)) {
        const hasMediaUrls = data.pagedOutputs.some(
          (item: unknown) => typeof item === 'string' && this.isMediaUrl(item)
        );
        if (hasMediaUrls) {
          delete data.pagedOutputs;
          delete data.currentPage;
        }
      }
      
      // Clear execution state only
      delete data.isLoading;
      delete data.error;
      
      return { ...node, data };
    });
  }
  
  /**
   * Check if a string is a media URL
   */
  private isMediaUrl(url: string): boolean {
    if (!url || typeof url !== 'string') return false;
    
    // Check for http/https URLs
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return false;
    }
    
    // Check for common media extensions or known CDN patterns
    const mediaExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.mp4', '.webm', '.mov', '.mp3', '.wav', '.ogg'];
    const lowerUrl = url.toLowerCase();
    
    if (mediaExtensions.some(ext => lowerUrl.includes(ext))) {
      return true;
    }
    
    // Check for R2 or CDN patterns
    if (url.includes('r2.cloudflarestorage.com') || 
        url.includes('fal.media') || 
        url.includes('googleapis.com') ||
        url.includes('blob:')) {
      return true;
    }
    
    return false;
  }
  
  /**
   * Get file extension from URL
   */
  private getExtensionFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const match = pathname.match(/\.[a-z0-9]+$/i);
      if (match) {
        return match[0].toLowerCase();
      }
    } catch {
      // Not a valid URL
    }
    
    // Default based on common patterns
    if (url.includes('video') || url.includes('.mp4')) return '.mp4';
    if (url.includes('audio') || url.includes('.mp3')) return '.mp3';
    return '.jpg'; // Default to jpg for images
  }
  
  /**
   * Sanitize filename for safe export
   */
  private sanitizeFilename(name: string): string {
    return name
      .replace(/[^a-z0-9\-_\s]/gi, '')
      .replace(/\s+/g, '-')
      .toLowerCase()
      .slice(0, 50) || 'untitled-knod';
  }
  
  /**
   * Trigger file download in browser
   */
  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

const knodExportService = new KnodExportService();
export default knodExportService;
